'use client';

import { createStore } from 'zustand/vanilla';
import { graphqlRequest } from '@/api/amplify/helpers';
import { vodConnectionByTypeKey } from '@/graphql/queries';
import {
  createVodConnection as createVodConnectionRequest,
  deleteVodConnection as deleteVodConnectionRequest,
} from '@/graphql/mutations';
import { useUserDataStore } from '@/stores/userDataStore';
import { vodConnection } from '@/types/entities';

export type vodConnectionState = {
  vodConnections: vodConnection[];
};

export type VodConnectionActions = {
  fetchByTypeKey: ({ appKey, id }: { appKey: string; id: string }) => Promise<vodConnection[]>;
  createVodConnection: (data: Partial<vodConnection>) => Promise<vodConnection[]>;
  deleteVodConnection: (id: string) => Promise<vodConnection[]>;
};

export type vodConnectionStore = vodConnectionState & VodConnectionActions;

export const getVodConnectionTypeKey = ({ appKey, id }) => {
  return `${appKey}_${id}`;
};

export const deleteVodConnection = async (get: () => vodConnectionStore, set, id: string) => {
  await graphqlRequest({
    query: deleteVodConnectionRequest,
    variables: {
      input: { id },
    },
  });

  const vodConnections = get()
    .vodConnections.filter((connection) => connection.id !== id)
    .map((connection) => connection);

  set({
    vodConnections,
  });

  return vodConnections;
};

export const createVodConnection = async (get: () => vodConnectionStore, set, data: Partial<vodConnection>) => {
  const { appKey, appUrl } = useUserDataStore.getState();

  const input = {
    ...data,
    appKey,
    appUrl,
  };

  const newConnection = await graphqlRequest({
    query: createVodConnectionRequest,
    variables: {
      input,
    },
  });

  const vodConnections = get().vodConnections.map((connection) => connection);
  vodConnections.push(newConnection.data.createVodConnection);

  set({
    vodConnections,
  });

  return vodConnections;
};

export const fetchByTypeKey = async (set, { appKey, id }) => {
  const typeKey = getVodConnectionTypeKey({ appKey, id });
  const vodConnection = await graphqlRequest({
    query: vodConnectionByTypeKey,
    variables: {
      typeKey,
    },
  });

  const vodConnections = vodConnection.data.vodConnectionByTypeKey.items;
  set({
    vodConnections,
  });
  return vodConnections;
};

export const initVodConnectionStore = (): vodConnectionState => {
  return {
    vodConnections: [] as vodConnection[],
  };
};

export const defaultInitState: vodConnectionState = {
  vodConnections: [] as vodConnection[],
};

export const createVodConnectionStore = () => {
  const store = createStore<vodConnectionStore>()((set, get) => ({
    ...defaultInitState,
    fetchByTypeKey: (value) => fetchByTypeKey(set, value),
    createVodConnection: (value) => createVodConnection(get, set, value),
    deleteVodConnection: (value) => deleteVodConnection(get, set, value),
  }));

  return store;
};
